import React, { createContext, useState, useContext, useEffect } from 'react';
import { API_URL as NODE_API_URL } from '../api';

const API_URL = 'http://localhost/kamas-vente/backend/api';
export const AUTH_URL = `${NODE_API_URL}/api/auth`;

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check localStorage on mount (for session persistence)
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedAuth = localStorage.getItem('isAuthenticated');
    if (storedUser && storedAuth === 'true') {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setIsAuthenticated(true);
      
      // DEBUG: Log stored user data
      console.log('Stored user data:', userData);
      console.log('Stored user role:', userData.role);
      
      // TEMPORARY: Force admin for specific email for testing
      const forceAdmin = userData.email === 'admin@achatventekamas.com';
      setIsAdmin(forceAdmin || userData.role === 'admin');
      
      console.log('Is admin (mounted):', forceAdmin || userData.role === 'admin');
    }
    fetchUsers();
    setLoading(false);
  }, []);

  // Fetch all users from API
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/users.php`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const login = async (email, password) => {
    let data;
    try {
      const response = await fetch(`${AUTH_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      data = await response.json();
    } catch (err) {
      throw new Error('Serveur injoignable, réessayez plus tard');
    }
    if (!data.success) {
      throw new Error(data.error || 'Email ou mot de passe incorrect');
    }

    // token : jeton de session signé, requis pour les actions admin (articles)
    const userData = { ...data.user, token: data.token };
    setUser(userData);
    setIsAuthenticated(true);
    
    // TEMPORARY: Force admin for specific email for testing
    const forceAdmin = userData.email === 'admin@achatventekamas.com';
    setIsAdmin(forceAdmin || userData.role === 'admin');
    
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('isAuthenticated', 'true');
    
    return userData;
  };

  const register = async (name, email, password, phone = '') => {
    try {
      let data;
      try {
        const response = await fetch(`${AUTH_URL}/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, password, phone })
        });
        data = await response.json();
      } catch (err) {
        throw new Error('Serveur injoignable, réessayez plus tard');
      }
      
      if (!data.success) {
        throw new Error(data.error || 'Registration failed');
      }
      
      // Auto-login after registration
      const userData = { ...data.user, token: data.token };
      setUser(userData);
      setIsAuthenticated(true);
      setIsAdmin(userData.role === 'admin');
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');
      
      return userData;
    } catch (err) {
      throw new Error(err.message || 'Registration failed');
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const addOrder = async (order) => {
    // This would be an API call in production
    if (user) {
      const updatedUser = { ...user, orders: [...(user.orders || []), order] };
      updateUser(updatedUser);
    }
  };

  // Generate multiple demo users at once (using MySQL)
  const generateBulkUsers = async (newUsersData) => {
    try {
      const response = await fetch(`${API_URL}/users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bulk: true, users: newUsersData })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Bulk creation failed');
      }
      
      await fetchUsers(); // Refresh users list
      return data.users || []; // Return the created users with their MySQL IDs
    } catch (err) {
      console.error('Error generating bulk users:', err);
      return 0;
    }
  };

  // Clear all demo data (remove demo users from DB)
  const clearAllDemoData = async () => {
    try {
      // Delete demo users (and their offers will cascade)
      const demoUsers = users.filter(u => u.is_demo);
      
      for (const demoUser of demoUsers) {
        await fetch(`${API_URL}/users.php?id=${demoUser.id}`, { method: 'DELETE' });
      }
      
      await fetchUsers();
      
      if (user?.is_demo) {
        logout();
      }
      
      return demoUsers.length;
    } catch (err) {
      console.error('Error clearing demo data:', err);
      return 0;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      users,
      isAuthenticated, 
      isAdmin,
      loading,
      login, 
      register, 
      logout,
      updateUser,
      addOrder,
      generateBulkUsers,
      clearAllDemoData,
      fetchUsers
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
